<script setup lang="ts">
import type inputFullPhone from '@/components/base/form/InputFullPhone.vue';
import type { UpdateUser } from '@/types/UserData';

type Props = {
  opacity?: boolean;
  allowGrow?: boolean;
  // rótulo alternativo do botão de cancelar edição (Minha Conta V2 usa "Voltar")
  cancelLabel?: string;
};

const {
  opacity = false,
  allowGrow = false,
  cancelLabel = '',
} = defineProps<Props>();

const emit = defineEmits(['check-pendency']);

const route = useRoute();
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { userId, userPhone, userPhoneDdi, fetchUserProfile } = useUserData();
const { isSmsVerified, isPhoneVerifiedOrComplete } = useUserValidations();
const { setPhoneToChange, resetPhoneToChange } = useUserActivityStore();
const { isModuleNeededOnFirstWithdraw } = useModulesValidation();
const { isSmsIntegrationEnabled } = useFeatureFlagsJson();
const { updateUserAccount } = useUserDataStore();

const inputPhoneRef = ref<InstanceType<typeof inputFullPhone> | null>(null);

const userPhoneNumber = computed<string>(() => {
  const unmaskedUserPhone = userPhone.value.replace(/\D/g, '');

  if (
    userPhoneDdi.value === '+55' &&
    unmaskedUserPhone.startsWith('55') &&
    unmaskedUserPhone.length > 11
  ) {
    return userPhoneDdi.value + unmaskedUserPhone.substring(2);
  }

  return userPhoneDdi.value + userPhone.value;
});

const selectedPhoneNumber = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneNumber ?? '';
});

const selectedPhoneDdi = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneDdi ?? '';
});

const isLoading = ref<boolean>(false);
const isEditing = ref<boolean>(false);

const isPhoneComplete = computed<boolean>(() => {
  return !!userPhoneDdi.value && !!userPhone.value;
});

const hasPendency = computed<boolean>(() => {
  return !isPhoneVerifiedOrComplete.value;
});

const badgeType = computed<'success' | 'error'>(() => {
  if (!isPhoneComplete.value) return 'error';
  if (!isSmsIntegrationEnabled.value) return 'success';
  if (!isSmsVerified.value) return 'error';

  return 'success';
});

const badgeLabel = computed<string>(() => {
  if (!isPhoneComplete.value) return t('account.incomplete');
  if (!isSmsIntegrationEnabled.value) return t('account.registered');
  if (!isSmsVerified.value) return t('account.not_verified');

  return t('account.verified');
});

const onSubmitPhoneVerify = async () => {
  await $containment.handle(
    [
      {
        name: 'phone',
        force: true,
      },
    ],
    () => {
      openToaster({
        message: t('account.phone_section.verify_success'),
        type: 'success',
      });

      isEditing.value = false;
      emit('check-pendency');
    },
  );
};

async function handleSubmit() {
  if (!selectedPhoneDdi.value || !selectedPhoneNumber.value) {
    openToaster({
      message: t('account.phone_section.empty'),
      type: 'error',
    });

    return;
  }

  if (!userPhone.value || !userPhoneDdi.value) {
    const { ApiService } = useApiService();

    isLoading.value = true;

    await ApiService.patch('/bff/users/add-phone', {
      phone: selectedPhoneNumber.value,
      ddi: selectedPhoneDdi.value,
    })
      .then(async () => {
        await fetchUserProfile({ force: true });

        openToaster({
          message: t('account.phone_section.update_success'),
          type: 'success',
        });
      })
      .catch((error) => {
        const errorMessage =
          error.data.message || t('account.phone_section.update_error');

        openToaster({
          message: errorMessage,
          type: 'error',
        });
      })
      .finally(() => {
        isLoading.value = false;
      });

    return;
  }

  await $containment.handle('validation_code_sms', () => {
    onEditPhoneSubmit();
  });
}

async function onEditPhoneSubmit() {
  if (!isSmsIntegrationEnabled.value) {
    const { $kyc } = useNuxtApp();
    const { temporaryPassword } = useModulesValidation();

    const sendData: UpdateUser = {
      phone: selectedPhoneNumber.value,
      ddi: selectedPhoneDdi.value,
      captcha_token: '',
    };

    if ($kyc?.current?.id) {
      sendData.kyc_id = $kyc.current.id;
    }

    if (temporaryPassword.get()) {
      sendData.password_check = temporaryPassword.get();
    }

    isLoading.value = true;

    await updateUserAccount(userId.value, sendData)
      .then(async () => {
        await fetchUserProfile({ force: true });

        openToaster({
          message: t('account.phone_section.update_success'),
          type: 'success',
        });

        isEditing.value = false;
      })
      .catch((error) => {
        const errorMessage =
          error.data.message || t('account.phone_section.update_error');

        openToaster({
          message: errorMessage,
          type: 'error',
        });
      })
      .finally(() => {
        isLoading.value = false;
        emit('check-pendency');
        temporaryPassword.clear();
      });

    return;
  }

  setPhoneToChange({
    ddi: selectedPhoneDdi.value,
    phone: selectedPhoneNumber.value,
  });

  await sleep(100);

  await $containment.handle(
    [
      {
        name: 'phone',
        force: true,
      },
    ],
    () => {
      openToaster({
        message: t('account.phone_section.update_success'),
        type: 'success',
      });

      isEditing.value = false;

      fetchUserProfile({ force: true });

      emit('check-pendency');
    },
  );
}

const toggleEdit = () => {
  if (isEditing.value) {
    resetPhoneToChange();
  }

  isEditing.value = !isEditing.value;
};

defineExpose({
  name: 'phone',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  if (route.query?.highlight === 'phone' && hasPendency.value) {
    isEditing.value = true;
  }
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      :class="[
        $style.container,
        { [$style.opacity]: opacity, [$style.grow]: allowGrow && !isEditing },
      ]"
    >
      <UserProfileDataCard
        :action-label="
          isEditing
            ? cancelLabel || $t('general.cancel')
            : isPhoneComplete
              ? $t('general.edit')
              : undefined
        "
        :action-type="!isEditing && isPhoneComplete ? 'edit' : undefined"
        :badge-label="badgeLabel"
        :badge-type="badgeType"
        :label="$t('account.phone_section.phone')"
        :required="isModuleNeededOnFirstWithdraw('phone')"
        @action="toggleEdit"
      >
        <template v-if="isPhoneComplete">
          {{ userPhoneNumber }}
        </template>
        <!-- CTA de cadastrar só faz sentido com o editor fechado -->
        <span
          v-else-if="!isEditing"
          :class="$style.registerBtn"
          @click.prevent="toggleEdit"
        >
          {{ $t('account.phone_section.register') }}
        </span>
        <template v-else>
          {{ $t('account.incomplete') }}
        </template>
        <button
          v-if="
            !isSmsVerified &&
            isPhoneComplete &&
            isSmsIntegrationEnabled &&
            !isEditing
          "
          :class="$style.verifyBtn"
          @click.prevent="onSubmitPhoneVerify"
        >
          {{ $t('account.phone_section.click_to_verify') }}
        </button>
      </UserProfileDataCard>

      <form
        v-if="isEditing"
        :class="$style.editContent"
        @submit.prevent="handleSubmit"
      >
        <div :class="$style.description">
          {{ $t('account.phone_section.check') }}
        </div>

        <div :class="$style.formGroup">
          <label for="phone">
            {{ $t('account.phone') }}
          </label>
          <BaseFormInputFullPhone
            id="phone"
            ref="inputPhoneRef"
            :required="true"
          />
        </div>

        <div class="flex justify-between items-end w-full col-span-2">
          <button
            v-if="!userPhone || !userPhoneDdi"
            :class="$style.btnSubmit"
            :disabled="!selectedPhoneDdi || !selectedPhoneNumber"
          >
            <NuxtIcon name="sync" />
            {{ $t('account.save') }}
          </button>
          <button
            v-else-if="
              selectedPhoneNumber === userPhone &&
              selectedPhoneDdi === userPhoneDdi &&
              !isSmsVerified &&
              isSmsIntegrationEnabled
            "
            :class="$style.btnSubmit"
            @click.prevent="onSubmitPhoneVerify"
          >
            <NuxtIcon name="check" />
            {{ $t('account.phone_section.verify') }}
          </button>
          <button
            v-else
            :class="$style.btnSubmit"
            :disabled="
              !selectedPhoneDdi ||
              !selectedPhoneNumber ||
              (selectedPhoneNumber === userPhone &&
                selectedPhoneDdi === userPhoneDdi)
            "
            type="submit"
          >
            <NuxtIcon name="sync" />
            {{ $t('account.phone_section.change') }}
          </button>
        </div>
      </form>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-3;
}

.grow {
  --card-grow: 1;
}

.opacity {
  @apply opacity-20 pointer-events-none select-none;
}

.registerBtn {
  @apply text-primary cursor-pointer;
}

.verifyBtn {
  @apply block text-xs text-primary mt-1 text-left;
}

.editContent {
  @apply w-full;

  .description {
    @apply text-sm text-texts/70 mb-4 max-w-[450px];
  }

  .formGroup {
    @apply mb-4 relative max-w-80;

    label {
      @apply text-xs font-medium;
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }
}
</style>
